import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import HomePage from '@/pages/HomePage';
// Components
import { CookieConsent } from '@/components/CookieConsent';
import { ChatWidget } from '@/components/ChatWidget';
import { LeadCapturePopup } from '@/components/LeadCapturePopup';
import { PriceAlertBanner } from '@/components/PriceAlertBanner';
import { Toaster } from '@/components/ui/sonner';
import '@/App.css';

// Initialize UTM tracking on app load
import { storeUTMParams } from '@/utils/analytics';
storeUTMParams();

// Lazy load non-critical pages
const ForexPage = lazy(() => import('@/pages/markets/ForexPage'));
const CommoditiesPage = lazy(() => import('@/pages/markets/CommoditiesPage'));
const IndicesPage = lazy(() => import('@/pages/markets/IndicesPage'));
const StocksPage = lazy(() => import('@/pages/markets/StocksPage'));
const CryptoPage = lazy(() => import('@/pages/markets/CryptoPage'));
const MT5Page = lazy(() => import('@/pages/platforms/MT5Page'));
const WebTraderPage = lazy(() => import('@/pages/platforms/WebTraderPage'));
const MobileTradingPage = lazy(() => import('@/pages/platforms/MobileTradingPage'));
const AccountTypesPage = lazy(() => import('@/pages/accounts/AccountTypesPage'));
const FundingPage = lazy(() => import('@/pages/accounts/FundingPage'));
const AccountComparePage = lazy(() => import('@/pages/accounts/AccountComparePage'));
const AboutPage = lazy(() => import('@/pages/company/AboutPage'));
const WhyUsPage = lazy(() => import('@/pages/company/WhyUsPage'));
const RegulationPage = lazy(() => import('@/pages/company/RegulationPage'));
const ContactPage = lazy(() => import('@/pages/company/ContactPage'));
const PartnersPage = lazy(() => import('@/pages/company/PartnersPage'));
const EconomicCalendarPage = lazy(() => import('@/pages/tools/EconomicCalendarPage'));
const CalculatorPage = lazy(() => import('@/pages/tools/CalculatorPage'));
const EducationPage = lazy(() => import('@/pages/tools/EducationPage'));
const AnalysisPage = lazy(() => import('@/pages/tools/AnalysisPage'));
const CoursePage = lazy(() => import('@/pages/tools/CoursePage'));
const TermsPage = lazy(() => import('@/pages/legal/TermsPage'));

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-white">
    <div className="flex flex-col items-center gap-3">
      <div className="w-8 h-8 border-3 border-gray-200 border-t-primary rounded-full animate-spin" />
      <span className="text-sm text-gray-400 font-medium">Loading...</span>
    </div>
  </div>
);

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            
            {/* Markets Routes */}
            <Route path="/markets/forex" element={<ForexPage />} />
            <Route path="/markets/commodities" element={<CommoditiesPage />} />
            <Route path="/markets/indices" element={<IndicesPage />} />
            <Route path="/markets/stocks" element={<StocksPage />} />
            <Route path="/markets/crypto" element={<CryptoPage />} />
            
            {/* Platforms Routes */}
            <Route path="/platforms/mt5" element={<MT5Page />} />
            <Route path="/platforms/web" element={<WebTraderPage />} />
            <Route path="/platforms/mobile" element={<MobileTradingPage />} />
            
            {/* Accounts Routes */}
            <Route path="/accounts/types" element={<AccountTypesPage />} />
            <Route path="/accounts/funding" element={<FundingPage />} />
            <Route path="/accounts/compare" element={<AccountComparePage />} />
            
            {/* Company Routes */}
            <Route path="/about" element={<AboutPage />} />
            <Route path="/why-us" element={<WhyUsPage />} />
            <Route path="/security" element={<RegulationPage />} />
            <Route path="/regulation" element={<RegulationPage />} />
            <Route path="/contact" element={<ContactPage />} />
            
            {/* Partners Route */}
            <Route path="/partners" element={<PartnersPage />} />
            
            {/* Tools Routes */}
            <Route path="/tools/economic-calendar" element={<EconomicCalendarPage />} />
            <Route path="/tools/calculator" element={<CalculatorPage />} />
            <Route path="/tools/education" element={<EducationPage />} />
            <Route path="/tools/analysis" element={<AnalysisPage />} />
            <Route path="/tools/education/:courseSlug" element={<CoursePage />} />

            {/* Legal Routes */}
            <Route path="/legal/terms" element={<TermsPage />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
      <PriceAlertBanner />
      <ChatWidget />
      <LeadCapturePopup />
      <CookieConsent />
      <Toaster position="top-right" />
    </div>
  );
}

export default App;
